import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLayoutContext } from '../Contexts/LayoutContext';
import { useLoginContext } from '../Contexts/LoginContext';
import { useStoreHook } from '../Hooks/StoreHook';
import DefaultLayout from '../Layouts/DefaultLayout';
import { formatCurrency, formatPhone } from '../Utils/Formatters';
import Img from '../Components/Layout/Img';
import CustomerService from '../Services/CustomerService';
import FidelityDashboard from './FidelityDashboard';
import { ImSpinner8 } from 'react-icons/im';
import { useLoginHook } from '../Hooks/LoginHook';
import TextInput from '../Components/Form/InputText';
import ButtonPrimary from '../Components/Form/ButtonPrimary';
import CardBoxFidelity from '../Components/Layout/CardBoxFidelity';
import Button from '../Components/Form/Button';
import FidelityHeader from './FidelityHeader';
import { IMaskInput } from 'react-imask';

const Fidelity = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { company, loadingCompany, isMobile } = useLayoutContext();
    const { getLinkToHome, loadCompanyInfo } = useStoreHook();
    const { user, setShowLoginBasic, setUser, refreshUser, isAuthenticated } = useLoginContext();
    const { handleSSO } = useLoginHook();
    const [loading, setLoading] = useState(false);
    const [fidelityExpireInfo, setFidelityExpireInfo] = useState<any>(null);
    const [activeSection, setActiveSection] = useState<'HOW_IT_WORKS' | 'CONFIRMATION' | 'TERMS' | 'QUESTIONS' | 'DASHBOARD'>('HOW_IT_WORKS');
    const [answers, setAnswers] = useState<Record<number, any>>({});
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        cpf: '',
        mobile_phone: ''
    });

    const determineSection = useCallback(() => {

        if (!isAuthenticated || !user?.logged) {
            setActiveSection('HOW_IT_WORKS');
            return;
        }

        // 1. Check Confirmation
        if (!user.is_confirmed) {
            setActiveSection('CONFIRMATION');
            setFormData({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                cpf: user.cpf || '',
                mobile_phone: user.mobile_phone || ''
            });
            return;
        }

        // 2. Check Terms Acceptance
        if (!user.on_fidelity_program) {
            setActiveSection('TERMS');
            return;
        }

        // 3. Check Questions
        const hasQuestions = (company?.fidelity?.questions?.length || 0) > 0;
        let allRequiredQuestionsAnswered = null;

        if (hasQuestions) {
            if (user?.fidelity_question_answers && user.fidelity_question_answers.length > 0) {
                allRequiredQuestionsAnswered = true;
                company.fidelity.questions.forEach((question: any) => {
                    if (question.required) {
                        const hasAnswer = user.fidelity_question_answers.find((v: any) => v.fidelity_question_id === question.id);
                        if (!hasAnswer) {
                            allRequiredQuestionsAnswered = false;
                        }
                    }
                });
            }
        }

        if (hasQuestions && allRequiredQuestionsAnswered === false) {
            setActiveSection('QUESTIONS');
            return;
        }

        setActiveSection('DASHBOARD');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, company]);

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            const customerService = new CustomerService('');
            const data = await customerService.getFidelityExpireInfo();
            setFidelityExpireInfo(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.fidelity_question_answers) {
            const initialAnswers: Record<number, any> = {};
            user.fidelity_question_answers.forEach((ans: any) => {
                initialAnswers[ans.fidelity_question_id] = {
                    id: ans.id,
                    answer: ans.answer,
                    fidelity_question_option_id: ans.fidelity_question_option_id
                };
            });
            setAnswers(initialAnswers);
        }
    }, [user?.fidelity_question_answers]);

    useEffect(() => {
        if (company) {
            handleSSO();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company]);

    useEffect(() => {
        if (isAuthenticated) {
            refreshUser();
        }
    }, [refreshUser, isAuthenticated]);

    // Initial check for section
    useEffect(() => {
        if (!company && !loadingCompany) {
            loadCompanyInfo();
            return;
        }

        if (company && (!company.fidelity || !company.fidelity.active)) {
            navigate(getLinkToHome());
        } else {
            determineSection();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company, loadingCompany, navigate, determineSection]);

    // Fetch data when section is DASHBOARD
    useEffect(() => {
        if (activeSection === 'DASHBOARD' && !fidelityExpireInfo) {
            loadDashboardData();
        }
    }, [activeSection, fidelityExpireInfo]);

    const handleConfirmProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setLoading(true);
            const customerService = new CustomerService('');
            const updatedUser = await customerService.confirmProfile(formData);
            if (user) {
                setUser({ ...user, ...updatedUser });
            }
            setActiveSection('TERMS');
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }

    const handleAcceptTerms = async () => {
        try {
            setLoading(true);
            const customerService = new CustomerService('');
            await customerService.acceptFidelityTerms();

            const hasQuestions = (company?.fidelity?.questions?.length || 0) > 0;
            let allRequiredQuestionsAnswered = false;

            if (hasQuestions) {
                if (user?.fidelity_question_answers && user.fidelity_question_answers.length > 0) {
                    allRequiredQuestionsAnswered = true;
                    company.fidelity.questions.forEach((question: any) => {
                        if (question.required) {
                            const hasAnswer = user.fidelity_question_answers.find((v: any) => v.fidelity_question_id === question.id);
                            if (!hasAnswer) {
                                allRequiredQuestionsAnswered = false;
                            }
                        }
                    });
                } else {
                    allRequiredQuestionsAnswered = false;
                }
            }

            if (user) {
                setUser({ ...user, on_fidelity_program: true });
                refreshUser();
            }

            if (hasQuestions && !allRequiredQuestionsAnswered) {
                setActiveSection('QUESTIONS');
            } else {
                setActiveSection('DASHBOARD');
            }

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }

    const handleAnswerQuestions = async (skip = false) => {
        try {
            setLoading(true);
            const questions = company.fidelity.questions;
            const payload: any[] = [];

            questions.forEach((q: any) => {
                if (skip) {
                    payload.push({
                        fidelity_question_id: q.id,
                        answer: '.'
                    });
                } else if (answers[q.id]?.answer) {
                    const ansPayload: any = {
                        fidelity_question_id: q.id,
                        answer: answers[q.id].answer
                    };

                    if (q.type === 2) {
                        ansPayload.fidelity_question_option_id = answers[q.id].fidelity_question_option_id;
                    }

                    if (answers[q.id]?.id) {
                        ansPayload.id = answers[q.id].id;
                    }

                    payload.push(ansPayload);
                }
            });

            const customerService = new CustomerService('');
            await customerService.answerFidelityQuestions(payload);
            
            if (user) {
                setUser({ ...user, on_fidelity_program: true });
                refreshUser();
            }
            setActiveSection('DASHBOARD');
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }

    const handleJoinProgram = () => {
        if (company?.sso_active) {
            const lang = localStorage.getItem('i18nextLng');
            const redirectPath = `${process.env.REACT_APP_URL_SSO_LOGIN!}?${new URLSearchParams({
                company: company.hash,
                redirect: window.location.pathname,
                ...(lang && { locale: lang }),
            }).toString()}`;

            localStorage.setItem('redirect', 'programa-de-fidelidade');
            window.location.href = redirectPath;
        } else {
            setShowLoginBasic(true);
        }
    };

    if (!company?.fidelity?.active && !loadingCompany) return null;

    if (loading || loadingCompany) {
        return (
            <DefaultLayout showHeader={!isMobile}>
                <div className="flex items-center justify-center h-full">
                    <ImSpinner8 className="animate-spin text-4xl" />
                </div>
            </DefaultLayout>
        );
    }

    return (
        <DefaultLayout showHeader={!isMobile}>
            <div className="w-full container max-w-[900px] mx-auto flex flex-col gap-6 items-center">

                {/* Banner */}
                {company.fidelity.banner_image_url && (
                    <div className="w-full rounded-lg overflow-hidden shadow-sm mt-4 md:mt-2">
                        <Img
                            src={company.fidelity.banner_image_url}
                            alt={company.fidelity.title || t('menu.main.fidelity.label')}
                            className="w-full h-auto object-cover max-h-[300px] md:max-h-[400px]"
                        />
                    </div>
                )}

                {/* Visual Content based on Section */}
                {activeSection === 'HOW_IT_WORKS' && (
                    <div className="w-full flex flex-col gap-10 md:gap-14 items-center transition-all ease-in-out">

                        <FidelityHeader
                            title="Como Funciona?"
                            subtitle="Aproveite as vantagens do nosso programa, não fique de fora dessa!"
                        />

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:mt-4 w-full max-w-5xl">
                            <CardBoxFidelity
                                step="1"
                                description={`Você acumula pontos a partir de <strong className="text-gray-800 font-bold">${formatCurrency(company.fidelity.minimum_order_value)}</strong> em compras`}
                            />

                            <CardBoxFidelity
                                step="2"
                                description={`
                                    A cada <strong className="text-gray-800 font-bold">${formatCurrency(company.fidelity.value_by_point)}</strong> em compras você acumula 
                                    <strong className="text-gray-800 font-bold">
                                        ${company.fidelity.points_by_value} ${company.fidelity.points_by_value === 1 ? 'ponto' : 'pontos'} 
                                        ${company?.fidelity?.currency_name ? `(${company?.fidelity?.currency_name})` : ''}</strong>`}
                            />

                            <CardBoxFidelity
                                step="3"
                                description={`1 ponto equivale à <strong className="text-gray-800 font-bold">${formatCurrency(company.fidelity.point_value)}</strong> em desconto nas suas compras`}
                            />
                        </div>

                        <div className="flex flex-wrap items-center gap-3 w-full md:w-1/2">
                            <ButtonPrimary onClick={handleJoinProgram} withLoading className="flex-1 text-nowrap flex-1">
                                Quero participar
                            </ButtonPrimary>

                            <Button onClick={() => navigate('/programa-de-fidelidade/termos-de-uso')} className="flex-1 text-nowrap">
                                Confira o regulamento
                            </Button>
                        </div>

                    </div>
                )}

                {activeSection === 'CONFIRMATION' && (
                    <div className="w-full flex flex-col gap-10 md:gap-14 items-center transition-all ease-in-out">

                        <FidelityHeader
                            title="Confirmação de dados"
                            subtitle="Para participar do programa de fidelidade você precisa confirmar os dados abaixo:"
                        />

                        <form onSubmit={handleConfirmProfile} className="w-full flex flex-col gap-4 text-left mt-4">
                            <div className="w-full flex flex-col md:flex-row gap-4">
                                <div className="flex flex-col gap-1 w-full">
                                    <label className="font-semibold text-shopfood-title">Qual o seu nome?</label>
                                    <TextInput
                                        value={formData.first_name}
                                        onChange={(e: any) => setFormData({ ...formData, first_name: e.target.value })}
                                        className="w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200 text-shopfood-title"
                                        required
                                    />
                                </div>
                                <div className="flex flex-col gap-1 w-full">
                                    <label className="font-semibold text-shopfood-title">E seu sobrenome?</label>
                                    <TextInput
                                        value={formData.last_name}
                                        onChange={(e: any) => setFormData({ ...formData, last_name: e.target.value })}
                                        className="w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200 text-shopfood-title"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col gap-1 w-full">
                                <label className="font-semibold text-shopfood-title">Informe seu email</label>
                                <TextInput
                                    type="email"
                                    value={formData.email}
                                    onChange={(e: any) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200 text-shopfood-title"
                                    required
                                />
                            </div>

                            <div className="w-full flex flex-col md:flex-row gap-4">
                                <div className="flex flex-col gap-1 w-full">
                                    <label className="font-semibold text-shopfood-title">Qual o número do seu celular?</label>
                                    <TextInput
                                        value={formatPhone(formData.mobile_phone)}
                                        onChange={(e: any) => setFormData({ ...formData, mobile_phone: e.target.value })}
                                        className="w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200 text-shopfood-title"
                                        required
                                    />
                                </div>
                                <div className="flex flex-col gap-1 w-full">
                                    <label className="font-semibold text-shopfood-title">Qual é o seu CPF?</label>
                                    <IMaskInput
                                        id="cpf"
                                        name="cpf"
                                        mask={"000.000.000-00"}
                                        value={formData.cpf}
                                        onAccept={(e: any) => setFormData({ ...formData, cpf: e?.target?.value })}
                                        unmask={true} // true|false|'typed'
                                        placeholder="___.___.___-__"
                                        className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                    />
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-3 w-full md:w-max">
                                <ButtonPrimary type='submit' onClick={() => {}} className="mt-2">
                                    Confirmar
                                </ButtonPrimary>
                            </div>

                        </form>
                    </div>
                )}

                {activeSection === 'TERMS' && (
                    <div className="w-full flex flex-col items-center gap-6">

                        <div className='flex flex-col gap-2 items-center'>
                            <FidelityHeader
                                title="Termos de uso"
                                subtitle={`Para participar do nosso programa de fidelidade, você precisa ler e aceitar os Termos de uso`}
                                description="Se você leu e concorda com os termos de uso, clique em continuar."
                            />
                        </div>

                        <hr className="w-full border-t border-gray-100" />

                        <div className="flex flex-wrap items-center gap-3 w-full md:w-1/2">
                            <ButtonPrimary onClick={handleAcceptTerms} className='flex-1'>
                                Continuar
                            </ButtonPrimary>

                            <Button onClick={() => navigate('/programa-de-fidelidade/termos-de-uso')} className="ring-offset-2 flex-1 text-nowrap">
                                Termos de uso
                            </Button>
                        </div>
                    </div>
                )}

                {activeSection === 'QUESTIONS' && (
                    <div className="w-full flex flex-col items-center gap-6 p-8">

                        <FidelityHeader
                            title="Deixe-nos saber um pouco mais sobre você"
                            subtitle="Responda as perguntas abaixo"
                        />

                        <form onSubmit={(e) => { e.preventDefault(); handleAnswerQuestions(false); }} className="w-full flex flex-col gap-6 mt-4">
                            {company.fidelity.questions.map((q: any, idx: number) => (
                                <div key={q.id} className="flex flex-col gap-2">
                                    <label className="font-semibold text-shopfood-title">{idx + 1}. {q.question?.replace(/\?$/, '')}?</label>

                                    {q.type === 1 ? (
                                        <TextInput
                                            value={answers[q.id]?.answer || ''}
                                            onChange={(e: any) => setAnswers({ ...answers, [q.id]: { ...answers[q.id], answer: e.target.value } })}
                                            placeholder="Sua resposta..."
                                            required={q.required}
                                        />
                                    ) : q.type === 2 && q.fidelity_question_options?.length > 0 ? (
                                        <select
                                            className="w-full border border-gray-300 rounded-lg p-3 text-gray-700 outline-none focus:ring-2 focus:ring-custom-primary bg-white h-[50px]"
                                            value={answers[q.id]?.fidelity_question_option_id || ''}
                                            onChange={(e) => {
                                                const selectedOption = q.fidelity_question_options.find((opt: any) => opt.id.toString() === e.target.value);
                                                setAnswers({ 
                                                    ...answers, 
                                                    [q.id]: { 
                                                        ...answers[q.id], 
                                                        fidelity_question_option_id: selectedOption?.id,
                                                        answer: selectedOption?.answer 
                                                    } 
                                                });
                                            }}
                                            required={q.required}
                                        >
                                            <option value="" disabled>Selecione uma opção...</option>
                                            {q.fidelity_question_options.map((opt: any) => (
                                                <option key={opt.id} value={opt.id}>{opt.answer}</option>
                                            ))}
                                        </select>
                                    ) : null}
                                </div>
                            ))}

                            <div className="flex flex-wrap items-center gap-3 w-full md:w-max">
                                <ButtonPrimary type='submit' onClick={() => {}} className="flex-1 text-nowrap flex-1">
                                    Responder
                                </ButtonPrimary>

                                {(!company?.fidelity?.questions?.some((q: any) => q.required)) && (
                                <Button onClick={() => handleAnswerQuestions(true)} className="ring-offset-2 flex-1 text-nowrap">
                                    Pular etapa
                                </Button>
                                )}
                            </div>

                        </form>
                    </div>
                )}

                {activeSection === 'DASHBOARD' && (
                    <FidelityDashboard fidelityExpireInfo={fidelityExpireInfo} />
                )}
            </div>
        </DefaultLayout>
    );
};

export default Fidelity;
